// RecipientMares.js — سجلّ الأفراس الحاضنة (v3.5.1، منقول عن Track B).
//
// لماذا صفحة إسناد مستقلّة ولا خانة في نموذج إدخال الخيل: كون الفرس حاضنة **دور موسميّ
// قابل للعكس** (تحضن هذا الموسم وقد تكون مانحة في القادم) بينما سجلّ الخيل هويّة دائمة.
// وهذه الصفحة تحاكي `StallionForm.js` عمدًا — النظام يحلّ المشكلة نفسها بالطريقة نفسها
// للأفحل منذ البداية، بما في ذلك دعم الفحل/الحاضنة من خارج المركز بلا سجلّ خيل.
//
// والسجلّ **لا يمنع** أيّ فرس من أن تكون حاضنة — يحدّد فقط من تظهر افتراضًا في قوائم
// اختيار الاستقبال. وتبقى الحاضنة فرسًا كاملة في الفحوصات والحمل والولادة والملكيّة.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArchiveIcon from '@mui/icons-material/Archive';
import EditIcon from '@mui/icons-material/Edit';
import PetsIcon from '@mui/icons-material/Pets';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestoreIcon from '@mui/icons-material/Restore';
import SettingsPageHeader from '../../components/SettingsPageHeader';
import api from '../../services/api';
import { SMART_COLORS as COLORS, smartCardSx as cardSx } from '../../theme/smartStableTheme';

const COPY = {
  ar: {
    title: 'إدارة الأفراس الحاضنة',
    subtitle: 'الأفراس المدرجة هنا هي التي تظهر عند اختيار الحاضنة في التزامن ونقل الجنين. وتبقى كل فرس ظاهرة كالمعتاد في الفحوصات والحمل والولادة والملكية.',
    add: 'إضافة حاضنة',
    editTitle: 'تعديل حاضنة',
    refresh: 'تحديث',
    registered: 'الحاضنات في الخدمة',
    registeredHint: 'هذه القائمة هي التي تظهر عند اختيار الحاضنة في التزامن ونقل الجنين.',
    inService: 'في الخدمة',
    total: 'حاضنات في الخدمة',
    inHouse: 'من المركز',
    external: 'مستأجرة',
    retired: 'خارج الخدمة',
    retiredHint: 'لا تظهر في قوائم الاختيار، وسجلاتها السابقة محفوظة كاملة. يمكن إعادتها للخدمة.',
    source: 'مصدر الحاضنة',
    inHouseHint: 'فرس مسجلة في سجل الخيل',
    externalHint: 'من خارج المركز، بلا سجل خيل عندنا',
    selectMare: 'اختر الفرس من الخيل المسجلة',
    eligible: 'فرس متاحة للإضافة',
    nameAr: 'الاسم بالعربية',
    nameEn: 'الاسم بالإنجليزية',
    country: 'بلد المنشأ',
    owner: 'المالك',
    chip: 'الشريحة',
    notes: 'ملاحظات',
    status: 'الحالة',
    actions: 'إجراءات',
    save: 'حفظ',
    cancel: 'إلغاء',
    edit: 'تعديل',
    retire: 'إخراج من الخدمة',
    reactivate: 'إعادة للخدمة',
    retireTitle: 'إخراج من الخدمة',
    retireReason: 'السبب (اختياري)',
    retireConfirm: 'ستختفي من قوائم اختيار الحاضنة فقط. سجلاتها السابقة تبقى كاملة — الفرس التي حضنت مولوداً جزء من سلسلة نسبه ولا تُحذف. ويمكن إعادتها للخدمة لاحقاً.',
    noReason: 'بلا سبب مسجل',
    noData: 'لا توجد حاضنات مسجلة بعد.',
    pickHorse: 'اختر الفرس من الخيل المسجلة.',
    needName: 'اسم الحاضنة المستأجرة مطلوب.',
    saved: 'تم حفظ الحاضنة.',
    retiredMsg: 'أُخرجت الحاضنة من الخدمة.',
    reactivatedMsg: 'أُعيدت الحاضنة إلى الخدمة.',
    duplicate: 'هذه الفرس مسجلة حاضنةً بالفعل.',
    loadError: 'تعذر تحميل سجل الحاضنات',
    saveError: 'تعذر حفظ الحاضنة.',
  },
  en: {
    title: 'Recipient mares',
    subtitle: 'Mares listed here appear when choosing a recipient for synchronization and embryo transfer. Every mare still appears as usual in exams, pregnancy, foaling and ownership.',
    add: 'Add recipient',
    editTitle: 'Edit recipient',
    refresh: 'Refresh',
    registered: 'Recipients in service',
    registeredHint: 'This is the list shown when choosing a recipient.',
    inService: 'in service',
    total: 'In service',
    inHouse: 'In-house',
    external: 'Rented',
    retired: 'Out of service',
    retiredHint: 'Hidden from pickers; past records are kept in full. Can be returned to service.',
    source: 'Recipient source',
    inHouseHint: 'A mare registered in the horse list',
    externalHint: 'From outside, with no horse record here',
    selectMare: 'Pick a mare from registered horses',
    eligible: 'mares available to add',
    nameAr: 'Name (Arabic)',
    nameEn: 'Name (English)',
    country: 'Origin country',
    owner: 'Owner',
    chip: 'Chip',
    notes: 'Notes',
    status: 'Status',
    actions: 'Actions',
    save: 'Save',
    cancel: 'Cancel',
    edit: 'Edit',
    retire: 'Retire',
    reactivate: 'Reactivate',
    retireTitle: 'Retire recipient',
    retireReason: 'Reason (optional)',
    retireConfirm: 'She disappears from recipient pickers only. Past records stay intact — a mare that carried a foal is part of its pedigree and is never deleted. She can be returned to service later.',
    noReason: 'No reason recorded',
    noData: 'No recipients registered yet.',
    pickHorse: 'Pick the mare from registered horses.',
    needName: 'A name is required for a rented recipient.',
    saved: 'Recipient saved.',
    retiredMsg: 'Recipient retired.',
    reactivatedMsg: 'Recipient returned to service.',
    duplicate: 'This mare is already registered as a recipient.',
    loadError: 'Could not load the recipient registry',
    saveError: 'Could not save the recipient.',
  },
};

const emptyForm = () => ({
  recipient_id: null,
  horse_id: null,
  name: '',
  name_ar: '',
  origin_country: '',
  owner_id: '',
  notes: '',
  is_inhouse: 1,
});

const rowName = (row) => {
  if (!row) return '-';
  return [row.horse_name_ar || row.name_ar, row.horse_name || row.name].filter(Boolean).join(' | ') || '-';
};

const candidateName = (horse) => {
  if (!horse) return '';
  return [horse.name_arabic, horse.name].filter(Boolean).join(' | ') || String(horse.horse_id);
};

export default function RecipientMares() {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'ar' ? 'ar' : 'en';
  const text = COPY[lang];

  const [mares, setMares] = useState([]);
  const [registered, setRegistered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [retireTarget, setRetireTarget] = useState(null);
  const [retireReason, setRetireReason] = useState('');
  const [form, setForm] = useState(emptyForm());
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const active = useMemo(() => registered.filter((r) => Number(r.is_active ?? 1) === 1), [registered]);
  const retired = useMemo(() => registered.filter((r) => Number(r.is_active ?? 1) !== 1), [registered]);

  const registeredHorseIds = useMemo(
    () => new Set(active.map((r) => Number(r.horse_id)).filter(Boolean)),
    [active],
  );
  const eligible = useMemo(
    () => mares.filter((h) => !registeredHorseIds.has(Number(h.horse_id)) || Number(h.horse_id) === Number(form.horse_id)),
    [form.horse_id, mares, registeredHorseIds],
  );

  const stats = useMemo(() => ({
    total: active.length,
    inHouse: active.filter((r) => Number(r.is_inhouse) === 1).length,
    external: active.filter((r) => Number(r.is_inhouse) !== 1).length,
    retired: retired.length,
  }), [active, retired]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // active=0 يجلب المتقاعدات أيضاً: تُعرَض في قسم منفصل، لأن الفرس التي حضنت مولوداً
      // تبقى جزءاً من سلسلة نسبه ولا تُحذف أبداً.
      const [maresRes, listRes] = await Promise.all([
        api.get('/mares'),
        api.get('/recipient-mares', { params: { active: 0 } }),
      ]);
      setMares(Array.isArray(maresRes.data) ? maresRes.data : []);
      setRegistered(Array.isArray(listRes.data) ? listRes.data : []);
    } catch (e) {
      setError(e?.response?.data?.message || text.loadError);
    } finally {
      setLoading(false);
    }
  }, [text.loadError]);

  useEffect(() => { loadData(); }, [loadData]);

  const openCreate = () => { setForm(emptyForm()); setError(''); setDialogOpen(true); };

  const openEdit = (row) => {
    setForm({
      recipient_id: row.recipient_id,
      horse_id: row.horse_id || null,
      name: row.name || '',
      name_ar: row.name_ar || '',
      origin_country: row.origin_country || '',
      owner_id: String(row.owner_id || ''),
      notes: row.notes || '',
      is_inhouse: Number(row.is_inhouse) === 1 ? 1 : 0,
    });
    setError('');
    setDialogOpen(true);
  };

  const save = async () => {
    const inhouse = Number(form.is_inhouse) === 1;
    if (inhouse && !form.horse_id) { setError(text.pickHorse); return; }
    if (!inhouse && !form.name && !form.name_ar) { setError(text.needName); return; }
    setSaving(true);
    setError('');
    setMessage('');
    const payload = {
      horse_id: inhouse ? form.horse_id : null,
      name: form.name || null,
      name_ar: form.name_ar || null,
      origin_country: form.origin_country || null,
      is_inhouse: inhouse,
      owner_id: inhouse ? Number(form.owner_id || 0) || null : null,
      notes: form.notes || null,
    };
    try {
      if (form.recipient_id) await api.put(`/recipient-mares/${form.recipient_id}`, payload);
      else await api.post('/recipient-mares', payload);
      setDialogOpen(false);
      setForm(emptyForm());
      setMessage(text.saved);
      await loadData();
    } catch (e) {
      setError(e?.response?.status === 409 ? text.duplicate : (e?.response?.data?.message || text.saveError));
    } finally {
      setSaving(false);
    }
  };

  const retire = async () => {
    if (!retireTarget) return;
    setSaving(true);
    setError('');
    try {
      await api.post(`/recipient-mares/${retireTarget.recipient_id}/retire`, { reason: retireReason || null });
      setRetireTarget(null);
      setRetireReason('');
      setMessage(text.retiredMsg);
      await loadData();
    } catch (e) {
      setError(e?.response?.data?.message || text.saveError);
    } finally {
      setSaving(false);
    }
  };

  const reactivate = async (row) => {
    setSaving(true);
    setError('');
    try {
      await api.post(`/recipient-mares/${row.recipient_id}/reactivate`, {});
      setMessage(text.reactivatedMsg);
      await loadData();
    } catch (e) {
      setError(e?.response?.status === 409 ? text.duplicate : (e?.response?.data?.message || text.saveError));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box className="smart-page-container" dir={lang === 'ar' ? 'rtl' : 'ltr'} sx={{ bgcolor: COLORS.page, minHeight: '100vh', maxWidth: 'var(--app-page-max)', mx: 'auto' }}>
      <SettingsPageHeader
        title={text.title}
        subtitle={text.subtitle}
        actions={(
          <Stack direction="row" spacing={1}>
            <Button onClick={loadData} startIcon={<RefreshIcon />} sx={{ color: COLORS.gold, fontWeight: 900 }}>{text.refresh}</Button>
            <Button onClick={openCreate} variant="contained" startIcon={<AddIcon />} sx={{ bgcolor: COLORS.gold, fontWeight: 900, '&:hover': { bgcolor: COLORS.goldHover } }}>{text.add}</Button>
          </Stack>
        )}
      />

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      <Grid container spacing={2.2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={3}><Metric icon={<PetsIcon />} label={text.total} value={stats.total} /></Grid>
        <Grid item xs={12} md={3}><Metric icon={<PetsIcon />} label={text.inHouse} value={stats.inHouse} color={COLORS.blue} soft={COLORS.blueSoft} /></Grid>
        <Grid item xs={12} md={3}><Metric icon={<PetsIcon />} label={text.external} value={stats.external} color={COLORS.green} soft={COLORS.greenSoft} /></Grid>
        <Grid item xs={12} md={3}><Metric icon={<ArchiveIcon />} label={text.retired} value={stats.retired} color={COLORS.rose} soft={COLORS.roseSoft} /></Grid>
      </Grid>

      <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} gap={1.5} sx={{ p: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>{text.registered}</Typography>
            <Typography variant="body2" sx={{ color: COLORS.muted }}>{text.registeredHint}</Typography>
          </Box>
          <Chip label={`${active.length} ${text.inService}`} sx={{ bgcolor: COLORS.goldSoft, color: COLORS.text, fontWeight: 900 }} />
        </Stack>

        <Box sx={{ overflowX: 'auto' }}>
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
            <Box component="thead" sx={{ bgcolor: COLORS.tableHead }}>
              <Box component="tr">
                {[text.nameAr, text.nameEn, text.source, text.chip, text.owner, text.status, ''].map((head, i) => (
                  <Box key={i} component="th" sx={{ p: 1.4, textAlign: 'start', color: '#fff', fontWeight: 900, whiteSpace: 'nowrap' }}>{head}</Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {active.length ? active.map((row) => (
                <Box key={row.recipient_id} component="tr" sx={{ borderTop: `1px solid ${COLORS.line}` }}>
                  <Cell strong>{row.horse_name_ar || row.name_ar || '-'}</Cell>
                  <Cell>{row.horse_name || row.name || '-'}</Cell>
                  <Cell>
                    <Chip
                      size="small"
                      label={Number(row.is_inhouse) === 1 ? text.inHouse : text.external}
                      sx={{ bgcolor: Number(row.is_inhouse) === 1 ? COLORS.blueSoft : COLORS.greenSoft, fontWeight: 900 }}
                    />
                  </Cell>
                  <Cell>{row.rfid_chip_number || '-'}</Cell>
                  <Cell>{(row.owner_name || '').trim() || row.owner_id || '-'}</Cell>
                  <Cell><Chip size="small" label={text.inService} sx={{ bgcolor: COLORS.greenSoft, fontWeight: 900 }} /></Cell>
                  <Cell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <IconButton size="small" onClick={() => openEdit(row)} title={text.edit} sx={{ color: COLORS.blue }}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => setRetireTarget(row)} title={text.retire} sx={{ color: COLORS.rose }}><ArchiveIcon fontSize="small" /></IconButton>
                    </Stack>
                  </Cell>
                </Box>
              )) : (
                <Box component="tr"><Cell colSpan={7} align="center">{text.noData}</Cell></Box>
              )}
            </Box>
          </Box>
        </Box>
      </Paper>

      {retired.length > 0 && (
        <Paper elevation={0} sx={{ ...cardSx, p: 2, mt: 2 }}>
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>{text.retired}</Typography>
            <Typography variant="body2" sx={{ color: COLORS.muted }}>{text.retiredHint}</Typography>
          </Box>
          <Grid container spacing={1.5}>
            {retired.map((row) => (
              <Grid item xs={12} sm={6} md={4} key={row.recipient_id}>
                <Paper elevation={0} sx={{ p: 1.4, border: `1px solid ${COLORS.line}`, bgcolor: COLORS.cardSoft }}>
                  <Typography sx={{ fontWeight: 900, color: COLORS.text }}>{rowName(row)}</Typography>
                  <Typography variant="caption" sx={{ color: COLORS.muted }}>{row.retire_reason || text.noReason}</Typography>
                  <Box sx={{ mt: 1, textAlign: 'end' }}>
                    <Button size="small" startIcon={<RestoreIcon />} disabled={saving} onClick={() => reactivate(row)} sx={{ color: COLORS.gold, fontWeight: 900 }}>
                      {text.reactivate}
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
        <DialogTitle sx={{ fontWeight: 900 }}>{form.recipient_id ? text.editTitle : text.add}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="caption" sx={{ color: COLORS.muted, fontWeight: 900 }}>{text.source}</Typography>
          <Grid container spacing={1.2} sx={{ mt: 0.2, mb: 1.5 }}>
            {[
              { value: 1, label: text.inHouse, hint: text.inHouseHint },
              { value: 0, label: text.external, hint: text.externalHint },
            ].map((opt) => {
              const selected = Number(form.is_inhouse) === opt.value;
              return (
                <Grid item xs={12} sm={6} key={opt.value}>
                  <Paper
                    elevation={0}
                    onClick={() => setForm((f) => ({ ...f, is_inhouse: opt.value, horse_id: null, owner_id: '', name: '', name_ar: '' }))}
                    sx={{ p: 1.4, cursor: 'pointer', border: `1px solid ${selected ? COLORS.gold : COLORS.line}`, bgcolor: selected ? COLORS.goldSoft : 'transparent' }}
                  >
                    <Typography sx={{ fontWeight: 900, color: COLORS.text }}>{opt.label}</Typography>
                    <Typography variant="caption" sx={{ color: COLORS.muted }}>{opt.hint}</Typography>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>

          {Number(form.is_inhouse) === 1 && (
            <>
              <Autocomplete
                options={eligible}
                value={mares.find((h) => Number(h.horse_id) === Number(form.horse_id)) || null}
                onChange={(_e, option) => setForm((f) => ({
                  ...f,
                  horse_id: option?.horse_id || null,
                  name: option?.name || '',
                  name_ar: option?.name_arabic || '',
                  owner_id: String(option?.owner_id || ''),
                }))}
                getOptionLabel={candidateName}
                isOptionEqualToValue={(o, v) => Number(o.horse_id) === Number(v?.horse_id)}
                renderInput={(params) => <TextField {...params} label={text.selectMare} />}
              />
              <Typography variant="caption" sx={{ color: COLORS.muted }}>{`${eligible.length} ${text.eligible}`}</Typography>
            </>
          )}

          <Grid container spacing={1.2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label={text.nameAr} value={form.name_ar} onChange={(e) => setForm((f) => ({ ...f, name_ar: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label={text.nameEn} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label={text.country} value={form.origin_country} onChange={(e) => setForm((f) => ({ ...f, origin_country: e.target.value }))} />
            </Grid>
            {Number(form.is_inhouse) === 1 && (
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={text.owner} value={form.owner_id} onChange={(e) => setForm((f) => ({ ...f, owner_id: e.target.value }))} />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={3} label={text.notes} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{text.cancel}</Button>
          <Button variant="contained" disabled={saving} onClick={save} sx={{ bgcolor: COLORS.gold, fontWeight: 900, '&:hover': { bgcolor: COLORS.goldHover } }}>{text.save}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(retireTarget)} onClose={() => setRetireTarget(null)} fullWidth maxWidth="xs" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
        <DialogTitle sx={{ fontWeight: 900 }}>{text.retireTitle}</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 1.5 }}>{text.retireConfirm}</Alert>
          <Typography sx={{ fontWeight: 900, mb: 1.5, color: COLORS.text }}>{rowName(retireTarget)}</Typography>
          <TextField fullWidth label={text.retireReason} value={retireReason} onChange={(e) => setRetireReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRetireTarget(null)}>{text.cancel}</Button>
          <Button variant="contained" color="error" disabled={saving} onClick={retire}>{text.retire}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function Metric({ icon, label, value, color = COLORS.gold, soft = COLORS.goldSoft }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2, display: 'flex', gap: 1.5, alignItems: 'center', minHeight: 104 }}>
      <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: soft, color, display: 'grid', placeItems: 'center' }}>{icon}</Box>
      <Box>
        <Typography variant="caption" sx={{ color, fontWeight: 900 }}>{label}</Typography>
        <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900 }}>{value}</Typography>
      </Box>
    </Paper>
  );
}

function Cell({ children, align = 'start', strong = false, colSpan }) {
  return (
    <Box component="td" colSpan={colSpan} sx={{ p: 1.4, color: COLORS.text, fontWeight: strong ? 900 : 500, textAlign: align, verticalAlign: 'middle' }}>
      {children}
    </Box>
  );
}
